import { useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

interface UploadFiscalDocumentsParams {
  invoiceId: string;
  companyId: string;
  danfePdf: File;
  xmlFile: File;
}

export function useUploadFiscalDocuments() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ invoiceId, companyId, danfePdf, xmlFile }: UploadFiscalDocumentsParams) => {
      // 1. Validar status da fatura (deve ser 'paid')
      const { data: invoice, error: fetchError } = await supabase
        .from('invoices')
        .select('status, invoice_number')
        .eq('id', invoiceId)
        .single();

      if (fetchError) throw fetchError;
      
      if (invoice.status !== 'paid') {
        throw new Error('Documentos fiscais só podem ser anexados a faturas pagas');
      }

      // 2. Upload do DANFE (PDF)
      const danfePath = `${companyId}/${invoiceId}/danfe.pdf`;
      const { error: danfeError } = await supabase.storage
        .from('fiscal-documents')
        .upload(danfePath, danfePdf, {
          cacheControl: '3600',
          upsert: true // Permite substituir arquivo existente
        });

      if (danfeError) throw new Error(`Erro ao fazer upload do DANFE: ${danfeError.message}`);

      // 3. Upload do XML
      const xmlPath = `${companyId}/${invoiceId}/nota.xml`;
      const { error: xmlError } = await supabase.storage
        .from('fiscal-documents')
        .upload(xmlPath, xmlFile, {
          cacheControl: '3600',
          upsert: true
        });

      if (xmlError) throw new Error(`Erro ao fazer upload do XML: ${xmlError.message}`);

      // 4. Armazenar apenas os paths (não URLs públicas - bucket é privado)
      const danfeStoragePath = danfePath;
      const xmlStoragePath = xmlPath;

      // 5. Atualizar registro da fatura com paths
      const { data: { user } } = await supabase.auth.getUser();
      
      const { error: updateError } = await supabase
        .from('invoices')
        .update({
          danfe_pdf_url: danfeStoragePath,
          xml_file_url: xmlStoragePath,
          fiscal_documents_uploaded_at: new Date().toISOString(),
          fiscal_documents_uploaded_by: user?.id
        })
        .eq('id', invoiceId);

      if (updateError) throw updateError;

      return {
        danfeUrl: danfeStoragePath,
        xmlUrl: xmlStoragePath,
        invoiceNumber: invoice.invoice_number
      };
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['invoices'] });
      queryClient.invalidateQueries({ queryKey: ['invoice', data.invoiceNumber] });
      
      toast.success('Documentos fiscais anexados!', {
        description: 'DANFE e XML estão disponíveis para download.',
      });
    },
    onError: (error: any) => {
      toast.error('Erro ao anexar documentos', {
        description: error.message,
      });
    },
  });
}
